import type { LucideIcon } from "lucide-react";
import { NavLink } from "react-router";
import { Icon } from "../icons/Icon";
import { cx, FOCUS_RING } from "../styles";

/**
 * The Driver App's tab bar (DA-10-15): a few destinations along the bottom of
 * the screen, each a 56px target — a gloved thumb on a moving van's dashboard
 * is the user.
 *
 * A router link per tab, so the back button and a shared link behave, and the
 * active tab is the router's answer rather than state kept here.
 */

export interface BottomTab {
	to: string;
	label: string;
	icon: LucideIcon;
}

export interface BottomTabBarProps {
	tabs: BottomTab[];
	/** The accessible name of the navigation landmark. */
	label: string;
}

export function BottomTabBar({ tabs, label }: BottomTabBarProps) {
	return (
		<nav
			aria-label={label}
			className="sticky bottom-0 z-10 grid border-t border-border bg-bg-surface pb-[env(safe-area-inset-bottom)]"
			style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
		>
			{tabs.map((tab) => (
				<NavLink
					key={tab.to}
					to={tab.to}
					className={({ isActive }) =>
						cx(
							"flex min-h-14 flex-col items-center justify-center gap-0.5 text-[12px] font-bold",
							FOCUS_RING,
							isActive ? "text-brand-ink" : "text-fg-muted",
						)
					}
				>
					<Icon as={tab.icon} size={22} />
					{tab.label}
				</NavLink>
			))}
		</nav>
	);
}
